import { snakeToCamel } from '@utils/format.utils'

import type { CreatePspToWalletDepositTransaction } from '@model/CreatePspToWalletDepositTransaction'
import type { PaymentProvider } from '@model/PaymentProviderDto'
import { CurrencyType } from '@model/WalletDto'

import type { DepositFormValues } from './DepositFlowLayout/types'
import type { DepositProceedValues } from './hooks/useDepositProceedRedirect'

export const getNormalizedWallet = (values: DepositFormValues) => {
  if (values.wallet) return values.wallet

  if (values.tradingAccount) {
    return {
      ...values.tradingAccount.wallet,
      client: values.tradingAccount.client,
      company: values.tradingAccount.company,
      depositAction: values.tradingAccount.depositAction,
      isDemo: values.tradingAccount.isDemo,
      currency: values.tradingAccount.currency,
      balance: values.tradingAccount.balance,
    }
  }

  return undefined
}

export const getCardholderFieldName = (provider?: any): string => {
  const fields = provider?.parameters?.fields ?? []
  const field = fields.find(
    (f: any) =>
      typeof f?.name === 'string' &&
      (f.name.endsWith('name_of_account') || f.name.endsWith('nameOfAccount'))
  )
  if (field) {
    return snakeToCamel(field.name.replace(/^fields\./, ''))
  }
  return 'nameOfAccount'
}

export const getDepositErrorMessage = (error: unknown): string => {
  if (typeof error === 'string' && error.trim()) {
    return error
  }

  const message = (
    error as {
      response?: { response?: { data?: { message?: unknown } } }
    }
  )?.response?.response?.data?.message

  return typeof message === 'string' && message.trim() ? message : 'Unknown error'
}

export const createDepositTransaction = (
  values: DepositFormValues,
  target: 'wallet' | 'trading-account'
): CreatePspToWalletDepositTransaction => {
  const base: CreatePspToWalletDepositTransaction = {
    fromPaymentProvider: {
      id: values.paymentProvider.id,
      currency: {
        id: values.paymentProvider.currency.id,
      },
    },
    toWallet: {
      id: target === 'wallet' ? values.wallet!.id : values.tradingAccount!.wallet.id,
      currency: {
        id: target === 'wallet' ? values.wallet!.currency.id : values.tradingAccount!.currency.id,
      },
    },
    amount: values.amount!,
    comment: values.comment,
    terms: values.terms.paymentOperations,
    form: values.paymentProvider.parameters.form || [],
    fields: values.fields,
    bonusCampaignId: values.isBonusSelected ? values.bonusCampaignId : '',
  }

  if (target === 'trading-account' && values.tradingAccount) {
    return {
      ...base,
      tradingAccount: {
        id: values.tradingAccount.id,
        campaign: {
          id: values.tradingAccount.campaign?.id,
        },
      },
    }
  }

  return base
}

export const isChinaUnionPayPSP = (providerId?: string): boolean =>
  [
    {
      id: '9599bfdb-825e-11eb-a2ad-06e70b382a0c',
      name: 'China UnionPay - Channel Z (SC Zotapay CNY (CUP))',
    },
    {
      id: '9017bcc6-939e-11ea-9c68-000c29df7a17',
      name: 'China UnionPay - Channel T (SC Transact365 CNY)',
    },
    {
      id: 'abfb42e9-9d79-11ed-8b0c-0a51dcb142a4',
      name: 'China Online Bank Transfer (SC Xpay CNY)',
    },
  ]
    .map((psp) => psp.id)
    .includes(providerId ?? '')

export function getDepositCurrency(values: DepositProceedValues): CurrencyType | undefined {
  if (values.fields?.paymentCurrencyId) {
    return values.fields.paymentCurrencyId as CurrencyType
  }

  if (values.wallet?.currency?.id) {
    return values.wallet.currency.id as CurrencyType
  }

  if (values.tradingAccount?.currency?.id) {
    return values.tradingAccount.currency.id as CurrencyType
  }

  return undefined
}

export const getCurrencyLimits = (
  paymentProvider: PaymentProvider
): { minAmount: number; maxAmount: number } => {
  const minAmount = paymentProvider.currency.minAmount || 0
  const maxAmount = paymentProvider.currency.maxAmount || 0

  return { minAmount, maxAmount }
}
